import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
  afterNextRender
} from '@angular/core';
import { debounce } from 'lodash';
import { ToastService } from 'src/app/services/toast.service';

// This component will wrap email labels and cut them off if the container is too short.
// Cuts off email with an ellipse: ...
@Component({
  selector: 'app-email-container',
  templateUrl: './email-container.component.html',
  styleUrls: ['./email-container.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmailContainerComponent {
  constructor(private ToastService: ToastService) {
    afterNextRender(() => {
      if (this.emailContainerHTML) {
        const { nativeElement } = this.emailContainerHTML;

        const { parentElement } = nativeElement;
        if (parentElement) {
          const { parentElement: grandpaElement } = parentElement;
          if (grandpaElement) {
            new ResizeObserver(
              debounce(() => {
                this.recursivelySnipEmailText(
                  Object.assign(nativeElement, { innerText: this.email }),
                  true
                );
              }, 25)
            ).observe(grandpaElement);
          }
        }
      }
    });
  }

  @ViewChild('emailContainer')
  emailContainerHTML?: ElementRef<HTMLLabelElement>;
  @Input() email!: string;

  // Check dom tree all the way to the root node. If any nodes have a smaller width than the
  // email label's width, we need to cut off the email label with an ellipse.
  recursivelyTravelDomToRoot(
    HTMLElement: HTMLElement,
    labelWidth: number,
    labelText: string
  ): boolean {
    const computedStyle = getComputedStyle(HTMLElement);
    const { paddingRight, paddingLeft } = computedStyle;
    const { parentElement, offsetWidth } = HTMLElement;

    const parsedPaddingRight = parseFloat(paddingRight);
    const parsedPaddingLeft = parseFloat(paddingLeft);

    const computedWidth = offsetWidth - parsedPaddingRight - parsedPaddingLeft;

    if (computedWidth < labelWidth && labelText.length !== 3) {
      return true;
    }

    if (parentElement) {
      return this.recursivelyTravelDomToRoot(
        parentElement,
        labelWidth,
        labelText
      );
    }

    return false;
  }

  recursivelySnipEmailText(HTMLElement: HTMLElement, isFirstRecurse: boolean) {
    const { offsetWidth, innerText } = HTMLElement;
    // Only travel up 2 nodes to observe the parent element of this component
    const isContainerSmallerThanEmail = this.recursivelyTravelDomToRoot(
      HTMLElement,
      offsetWidth,
      innerText
    );
    if (isContainerSmallerThanEmail) {
      Object.assign(HTMLElement, {
        innerText: isFirstRecurse
          ? innerText
              .slice(0, Math.floor(innerText.length * 0.75))
              .concat('...')
          : innerText.slice(0, (innerText.length - 3) * 0.75).concat('...')
      });
      this.recursivelySnipEmailText(HTMLElement, false);
    }
    return;
  }

  ngAfterViewInit(): void {
    if (!this.emailContainerHTML) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing email container element.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.emailContainerHTML;
    this.recursivelySnipEmailText(
      Object.assign(nativeElement, { innerText: this.email }),
      true
    );
  }

  ngOnInit(): void {
    if (!this.email) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing email information.',
        { returnError: true }
      );
    }
  }
}
